// @ts-ignore
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import sugang from 'src/assets/images/sugang.png';
import { ReactComponent as Logo } from 'src/assets/svg/logo.svg';
import { Blank, Section } from 'src/components/common';
import { externalCreateToken, externalUpdateQueue } from 'src/generated/endpoint';
import { childState, toastState } from 'src/store';

export function CourseEntrancePage() {
  const { push } = useHistory();
  const location = useLocation();

  const myChild = useRecoilValue(childState);

  const [waiting, setWaiting] = useState(false);
  const [waitingPosition, setWaitingPosition] = useState(0);
  const [waitingTime, setWaitingTime] = useState(0);

  const [toastMsg, setToastMsg] = useRecoilState(toastState);
  const params = new URLSearchParams(location.search);

  const [isJumping, setIsJumping] = useState(false);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setIsJumping(false);

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  /** 시간 1초씩 빼주기 */
  useEffect(() => {
    if (waitingTime <= 0) return; // 0 이하면 타이머 중단

    const interval = setInterval(() => {
      setWaitingTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // cleanup 함수: 컴포넌트 언마운트 시 타이머 해제
    return () => clearInterval(interval);
  }, [waitingTime]);

  function openSugang(retryCount = 0) {
    if (!waiting) {
      setWaiting(true);
    }

    externalUpdateQueue({
      headers: { 'child-user-id': myChild?.id },
      skipRefetch: true,
    })
      .then((ticket: any) => {
        if (ticket && retryCount < 3) {
          if (ticket.status === 'ready') {
            externalCreateToken({
              headers: { 'child-user-id': myChild?.id },
            })
              .then((token) => {
                setIsJumping(true); // 수강신청 이동 대기화면 표시
                if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.startsWith('http://')) {
                  // 로컬
                  window.open(`http://localhost:3300/course/student?sso=${token}`, '_self');
                } else {
                  window.open(`https://${window.location.hostname}/course/student?sso=${token}`, '_self');
                }
              })
              .catch(() => {
                alert('수강신청 접속에 실패했습니다. 잠시후 다시 시도해주세요.');
                push('/student/canteen');
              });
          } else {
            setWaitingPosition(ticket.position);
            setWaitingTime(ticket.estimatedWaitTime);
            retryTimerRef.current = setTimeout(() => {
              openSugang(retryCount);
            }, 5000);
          }
        } else {
          alert('수강신청 대기 번호를 받지 못했습니다.');
          push('/student/canteen');
        }
      })
      .catch(() => {
        if (retryCount < 3) {
          setToastMsg('수강신청 대기번호를 다시 요청합니다.');
          retryTimerRef.current = setTimeout(() => {
            openSugang(retryCount + 1);
          }, 5000);
        } else {
          alert('수강신청 대기 번호를 받지 못했습니다.');
          push('/student/canteen');
        }
      });
  }

  return (
    <>
      {isJumping && <Blank text="수강신청 화면으로 이동합니다. 잠시만 기다려주세요." />}
      <div className="mx-10">
        {waiting ? (
          <>
            <Section className="mt-20">
              <div className="flex justify-center">
                <Logo className="w-[70px]" />
              </div>

              <div className="text-center text-2xl font-bold">
                접속자가 많아 대기 중 입니다
                <br />
                잠시만 기다려주세요
              </div>
            </Section>
            <Section className="mt-5">
              <div className="flex w-full justify-center">
                <div className="h-28 w-60 rounded-xl bg-gray-50 p-5 text-center">
                  <span className="text-base">현재 대기인원</span>
                  <br />
                  <div className="flex items-center justify-center space-x-1 text-center">
                    <span className="text-[32px] font-bold text-orange-400">{waitingPosition}</span>
                    <span className="text-base text-gray-400">명</span>
                  </div>
                </div>
              </div>
              <div className="text-center text-15 text-gray-400">
                <span>예상 대기시간은 </span>
                <span className="text-orange-400">{waitingTime}초</span>
                <span>입니다.</span>
              </div>
            </Section>

            <Section className="fixed bottom-30 left-1/2 -translate-x-1/2 transform">
              <div
                onClick={() => {
                  push('/student/canteen');
                }}
                className="h-12 w-40 cursor-pointer rounded-md border border-gray-400 py-3 text-center text-base"
              >
                슈퍼스쿨로 돌아가기
              </div>
            </Section>
          </>
        ) : (
          <>
            <Section className="mt-20">
              <Logo className="w-[70px] opacity-50" />
            </Section>
            <Section className="mt-2">
              <div
                onClick={() => {
                  openSugang();
                }}
                className="flex h-24 cursor-pointer items-center justify-between rounded-xl border border-orange-400 p-5 shadow-lg"
              >
                <div className="flex">
                  <img className="mx-auto h-6 w-auto" src={sugang} alt="" />
                  <div className="ml-2">
                    <span className="text-18 font-bold text-orange-500">신청 바로가기</span>
                    <br />
                    <span className="text-14 text-gray-500">슈퍼스쿨에서 쉽고 편하게!</span>
                  </div>
                </div>
                <div>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M6 13L10.6447 8.40105C10.8409 8.20675 10.8425 7.89016 10.6482 7.69393L6 3"
                      stroke="#FFBC99"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <div
                onClick={() => {
                  push('/student/canteen');
                }}
                className="flex h-[76px] cursor-pointer items-center justify-between rounded-xl border border-gray-400 p-5 text-lg font-bold"
              >
                <div className="flex">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="24" height="24" rx="12" fill="#898D94" />
                    <path
                      d="M12.0379 5L7.71379 9.31351L9.00345 10.6L12.0379 7.57297L14.9966 10.5243L16.2862 9.23784L12.0379 5Z"
                      fill="white"
                    />
                    <path d="M6.5 13.4757V11.8108H11.2034V16.8811H9.38276V13.4757H6.5Z" fill="white" />
                    <path d="M17.5 13.4757V11.8108H12.7966V19H14.6172V13.4757H17.5Z" fill="white" />
                  </svg>
                  <span className="ml-2 text-18 font-bold text-gray-500">슈퍼스쿨 바로가기</span>
                </div>
                <div>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M6 13L10.6447 8.40105C10.8409 8.20675 10.8425 7.89016 10.6482 7.69393L6 3"
                      stroke="#C7CBD1"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </Section>
          </>
        )}
      </div>
    </>
  );
  //}
}
